// chart imports
import { BubbleDataPoint, Chart, ChartTypeRegistry, Point, TooltipModel } from "chart.js";

// configuration
import Config from "./Config";

// utils
import { getColor } from "../../../../../../../../../utils/getColor";
import { truncateText } from "../utils";

// types
import { IScatterPoint } from "../ScatterChart/useScatterChart";

// schema
export type ChartArgument = Chart<
	keyof ChartTypeRegistry,
	(number | Point | [number, number] | BubbleDataPoint | null)[],
	unknown
>;
export type TooltipArgument = TooltipModel<"scatter">;

/**
 * Get or create the tooltip element for the chart
 */
const getOrCreateTooltip = (chart: ChartArgument): HTMLDivElement => {
	let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLDivElement>("div.tooltip");

	if (tooltipEl) {
		// remove the caret from the tooltip and re-add since it could be on bottom now
		const caret = tooltipEl.querySelector<HTMLDivElement>("div#caret");
		if (caret) {
			caret.remove();
		}
	} else {
		// otherwise create a new element to use for tooltip
		tooltipEl = createTooltipEl();
		chart.canvas.parentNode?.appendChild(tooltipEl);
	}

	return tooltipEl;
};

/**
 * Create an empty tooltip element with the necessary styles
 */
const createTooltipEl = (): HTMLDivElement => {
	const tooltipEl = document.createElement("div");
	tooltipEl.classList.add("tooltip");
	tooltipEl.style.background = getColor("neutral", 7);
	tooltipEl.style.borderRadius = "4px";
	tooltipEl.style.color = "white";
	tooltipEl.style.pointerEvents = "none";
	tooltipEl.style.position = "absolute";
	tooltipEl.style.zIndex = "1";
	// make invisible at first
	tooltipEl.style.opacity = "0";

	// match animation speed of chart.js tooltip
	tooltipEl.style.transition = "all .3s ease-in-out, opacity .2s linear";

	const tooltipArea = document.createElement("div");
	tooltipArea.style.margin = "0px";
	tooltipArea.id = "tooltip-area";

	tooltipEl.appendChild(tooltipArea);

	return tooltipEl;
};

/**
 * Create a caret for the tooltip that points to the data point,
 * above or below the tooltip depending on the distance from
 * the top and bottom edges of the chart.
 */
const createCaret = (tooltip, newTooltipEl, distances, translatePercent) => {
	const { distanceMinY, distanceMaxY } = distances;

	// create caret for tooltip that is pointing to the data point
	// and color should match the tooltip background color
	const caret = document.createElement("div");
	caret.id = "caret";
	caret.style.position = "absolute";
	caret.style.border = "none";

	// set the caret to be the same color as the tooltip background
	caret.style.background = newTooltipEl.style.background;

	// shape the div not the border into a triangle pointing up
	caret.style.width = `${Config.CARET_SIZE}px`;
	caret.style.height = `${Config.CARET_SIZE}px`;

	// if closer to the top of the chart than the bottom
	if (distanceMinY < distanceMaxY) {
		// move it to the bottom edge of the tooltip
		caret.style.bottom = `-${tooltip.options.padding / 2}px`;

		// move it to the center
		caret.style.left = `calc(${translatePercent}% - ${tooltip.options.padding / 2}px)`;

		// rotate and clip the top of the caret
		caret.style.transform = "rotate(135deg)";
		caret.style.clipPath = "polygon(0 0, 100% 0, 100% 100%)";
	} else {
		// move it to the top edge of the tooltip
		caret.style.top = `-${tooltip.options.padding / 2}px`;

		// move it to the center
		caret.style.left = `calc(${translatePercent}% - ${tooltip.options.padding / 2}px)`;

		// rotate and clip the bottom of the caret
		caret.style.transform = "rotate(45deg)";
		caret.style.clipPath = "polygon(0 0, 100% 0, 0 100%)";
	}
	return caret;
};

/**
 * Calculate and return the distance from the tooltip to each edge of the chart
 */
const mapTooltipDistances = (
	scales,
	averageX,
	averageY
): { distanceMinX: number; distanceMaxX: number; distanceMinY: number; distanceMaxY: number } => {
	const { x, y } = scales;
	const minX = x.left;
	const maxX = x.right;
	const minY = y.bottom;
	const maxY = y.top;

	// get the distance from the tooltip to each edge of the chart
	const distanceMinX = Math.abs(averageX - minX);
	const distanceMaxX = Math.abs(averageX - maxX);
	const distanceMinY = Math.abs(averageY - minY);
	const distanceMaxY = Math.abs(averageY - maxY);

	return { distanceMinX, distanceMaxX, distanceMinY, distanceMaxY };
};

/**
 * Position the tooltip based on the distances from the edges of the chart
 */
const positionTooltip = (
	tooltipEl: HTMLDivElement,
	distances: { [key: string]: number },
	chart: ChartArgument,
	averageX: number,
	averageY: number
) => {
	const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

	// position tooltip
	const { distanceMinY, distanceMaxY, distanceMinX, distanceMaxX } = distances;

	// measure the current tooltip width
	const tooltipWidth = tooltipEl.clientWidth;

	let translatePercent = Config.TRANSLATE_PERCENT.DEFAULT;
	let verticalPixelOffset = Config.CARET_SIZE;

	if (distanceMinY < distanceMaxY) {
		// place above
		verticalPixelOffset = -Config.CARET_SIZE;
		tooltipEl.style.left = `${positionX + averageX}px`;
		tooltipEl.style.top = `${positionY + averageY - tooltipEl.clientHeight - 24}px`;
	} else {
		tooltipEl.style.left = `${positionX + averageX}px`;
		tooltipEl.style.top = `${positionY + averageY + 24}px`;
	}
	if (distanceMaxX < tooltipWidth / 2) {
		// shift to the right
		translatePercent = Config.TRANSLATE_PERCENT.RIGHT;
	} else if (distanceMinX < tooltipWidth / 2) {
		// shift to the left
		translatePercent = Config.TRANSLATE_PERCENT.LEFT;
	}
	tooltipEl.style.transform = `translate(-${translatePercent}%, ${verticalPixelOffset}px)`;

	return { translatePercent };
};

const displayIndividuals = (
	tooltip: TooltipArgument,
	tooltipEl: HTMLDivElement,
	chart: ChartArgument,
	labels: {
		xAxisLabel: string;
		xAggregateLabel?: string;
		yAxisLabel: string;
		yAggregateLabel?: string;
	}
) => {
	const expandableColumns = document.createElement("div");
	expandableColumns.classList.add("tooltip-columns");

	const individuals = tooltip.dataPoints.map((b) => b.raw as IScatterPoint);

	individuals.forEach((dataPoint, inx) => {
		// limit to only max points individuals per tooltip
		if (inx >= Config.MAX_POINTS) return;
		const individual = document.createElement("div");
		individual.classList.add("individual");
		individual.style.maxWidth = `${Config.MAX_WIDTH_COLUMN}px`;

		// add margin bottom to all but the last individual for each column
		if (inx + 1 < individuals.length && (inx + 1) % Config.MAX_PER_COLUMN !== 0) {
			individual.style.marginBottom = "8px";
		}

		// name for the individual data point
		const individualName = document.createElement("div");
		const xAxis = document.createElement("div");
		const yAxis = document.createElement("div");

		// truncate the name if it is too long
		const nameText = truncateText(chart.ctx, dataPoint.name, Config.MAX_WIDTH_COLUMN - 32);

		individualName.textContent = nameText;
		individualName.style.fontSize = Config.FONTS.NAME.SIZE;
		individualName.style.lineHeight = Config.FONTS.NAME.LINE_HEIGHT;
		individualName.style.fontWeight = Config.FONTS.NAME.WEIGHT;

		// fixed text for x and y axis wont be truncated
		// TODO: use large number abbreviation for really large numbers
		const fixedTextX = `${labels.xAggregateLabel || ""}: ${parseFloat(dataPoint.x.toFixed(2))}`;
		const fixedTextY = `${labels.yAggregateLabel || ""}: ${parseFloat(dataPoint.y.toFixed(2))}`;

		let textWidthX = 0;
		let textWidthY = 0;

		textWidthX = chart.ctx.measureText(fixedTextX).width;
		textWidthY = chart.ctx.measureText(fixedTextY).width;

		const availableWidthX = Config.MAX_WIDTH_COLUMN - textWidthX - 32;
		const availableWidthY = Config.MAX_WIDTH_COLUMN - textWidthY - 32;

		xAxis.textContent = `${truncateText(chart.ctx, labels.xAxisLabel, availableWidthX)} ${fixedTextX}`;
		xAxis.style.fontSize = Config.FONTS.VALUE.SIZE;
		xAxis.style.lineHeight = Config.FONTS.VALUE.LINE_HEIGHT;
		xAxis.style.fontWeight = Config.FONTS.VALUE.WEIGHT;

		yAxis.textContent = `${truncateText(chart.ctx, labels.yAxisLabel, availableWidthY)} ${fixedTextY}`;
		yAxis.style.fontSize = Config.FONTS.VALUE.SIZE;
		yAxis.style.lineHeight = Config.FONTS.VALUE.LINE_HEIGHT;
		yAxis.style.fontWeight = Config.FONTS.VALUE.WEIGHT;

		individual.appendChild(individualName);
		individual.appendChild(xAxis);
		individual.appendChild(yAxis);

		expandableColumns.appendChild(individual);
	});

	const tooltipRoot = tooltipEl.querySelector<HTMLDivElement>("div#tooltip-area");

	// Remove old children
	while (tooltipRoot?.firstChild) {
		tooltipRoot.firstChild.remove();
	}

	// Add new children
	tooltipRoot?.appendChild(expandableColumns);

	// make a footnote if we have more than 10 individuals
	if (individuals.length > 10) {
		const tooltipFootnote = document.createElement("div");
		tooltipFootnote.classList.add("tooltip-footnote");
		tooltipFootnote.textContent = Config.getMessageContent(individuals.length, individuals[0].type);
		tooltipFootnote.style.fontSize = Config.FONTS.MESSAGE.SIZE;
		tooltipFootnote.style.lineHeight = Config.FONTS.MESSAGE.LINE_HEIGHT;
		tooltipFootnote.style.fontWeight = Config.FONTS.MESSAGE.WEIGHT;

		// Add new children
		tooltipRoot?.appendChild(tooltipFootnote);
	}
};

/**
 * Create the external tooltip for the chart, with the data for each individual.
 * We are able to display up to 10 individuals in the tooltip.
 */
export const createExternalTooltip = (
	context: {
		chart: ChartArgument;
		tooltip: TooltipArgument;
	},
	labels: { xAxisLabel: string; xAggregateLabel?: string; yAxisLabel: string; yAggregateLabel?: string }
) => {
	// Tooltip Element
	const { chart, tooltip } = context;

	const tooltipEl = getOrCreateTooltip(chart);

	// Hide if no tooltip
	if (tooltip.opacity === 0) {
		tooltipEl.style.opacity = "0";
		return;
	}

	displayIndividuals(tooltip, tooltipEl, chart, labels);

	// get the distance from the tooltip to each edge of the chart
	const distances = mapTooltipDistances(chart.scales, tooltip.caretX, tooltip.caretY);

	const { translatePercent } = positionTooltip(tooltipEl, distances, chart, tooltip.caretX, tooltip.caretY);

	const caret = createCaret(tooltip, tooltipEl, distances, translatePercent);
	// append the caret to the tooltip
	tooltipEl.appendChild(caret);

	// Display, position, and set styles for font
	tooltipEl.style.font = "Source Sans Pro, sans-serif";
	tooltipEl.style.padding = tooltip.options.padding + "px " + tooltip.options.padding + "px";
	tooltipEl.style.opacity = "1";
};

export default createExternalTooltip;
